import { createContext, useContext, useState, useRef, type Dispatch, type ReactNode, type SetStateAction } from 'react';

interface TTSContextValue {
  currentAudio: HTMLAudioElement | null;
  setCurrentAudio: (audio: HTMLAudioElement | null) => void;
  stopCurrent: () => void;
  currentSegmentIndex: number;
  setCurrentSegmentIndex: Dispatch<SetStateAction<number>>;
  currentSegmentRange: { start: number; end: number };
  setCurrentSegmentRange: Dispatch<SetStateAction<{ start: number; end: number }>>;
  textSegments: string[];
  setTextSegments: Dispatch<SetStateAction<string[]>>;
  fullExtractedText: string;
  setFullExtractedText: Dispatch<SetStateAction<string>>;
  currentWordIndex: number;
  setCurrentWordIndex: Dispatch<SetStateAction<number>>;
}

const TTSContext = createContext<TTSContextValue | undefined>(undefined);

export function TTSProvider({ children }: { children: ReactNode }) {
  const [currentAudio, setCurrentAudioState] = useState<HTMLAudioElement | null>(null);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState<number>(-1);
  const [currentSegmentRange, setCurrentSegmentRange] = useState<{ start: number; end: number }>({ start: -1, end: -1 });
  const [textSegments, setTextSegments] = useState<string[]>([]);
  const [fullExtractedText, setFullExtractedText] = useState<string>('');
  const [currentWordIndex, setCurrentWordIndex] = useState<number>(-1);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const setCurrentAudio = (audio: HTMLAudioElement | null) => {
    // Stop any previous audio before setting new one
    if (audioRef.current && audioRef.current !== audio) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    audioRef.current = audio;
    setCurrentAudioState(audio);
  };

  const stopCurrent = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setCurrentAudioState(null);
    }
    setCurrentSegmentIndex(-1);
    setCurrentSegmentRange({ start: -1, end: -1 });
    setTextSegments([]);
    setFullExtractedText('');
    setCurrentWordIndex(-1);
  };

  return (
    <TTSContext.Provider value={{
      currentAudio,
      setCurrentAudio,
      stopCurrent,
      currentSegmentIndex,
      setCurrentSegmentIndex,
      currentSegmentRange,
      setCurrentSegmentRange,
      textSegments,
      setTextSegments,
      fullExtractedText,
      setFullExtractedText,
      currentWordIndex,
      setCurrentWordIndex
    }}>
      {children}
    </TTSContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTTS() {
  const context = useContext(TTSContext);
  if (!context) {
    throw new Error('useTTS must be used within TTSProvider');
  }
  return context;
}
